import { signedRelativeDelta } from './math.js';

const DEFAULT_TOLERANCE = {
  energyWarn: 2e-3,
  energyCritical: 3e-2,
  momentumWarn: 2e-6,
  momentumCritical: 2e-3,
};

function severityOf(defect, warn, critical) {
  if (defect >= critical) return 'critical';
  if (defect >= warn) return 'warning';
  return 'ok';
}

function makeMetric({ mode, current, reference, signedDelta, absoluteDefect, tolerances, label }) {
  const severity = severityOf(absoluteDefect, tolerances.warn, tolerances.critical);
  return {
    mode,
    label,
    current,
    reference,
    delta: signedDelta,
    defect: absoluteDefect,
    tolerance: tolerances.warn,
    criticalTolerance: tolerances.critical,
    severity,
    isViolation: severity !== 'ok',
  };
}

/**
 * 用初始基线检查当前状态的守恒情况。
 * - conserved：允许的绝对相对偏差，超过阈值告警。
 * - dissipative：只惩罚“无中生有”的能量增加，正常耗散不告警。
 */
export function evaluateConservation(model, params, state, baseline) {
  const metrics = model.measure(state, params);
  const policy = model.conservation?.(params) ?? {};
  const tolerances = { ...DEFAULT_TOLERANCE, ...(model.tolerance ?? {}) };
  const report = {
    level: 'ok',
    ok: true,
    energy: null,
    momentum: null,
    warnings: [],
    policy,
  };

  if (metrics.energy && policy.energy) {
    const current = metrics.energy.total;
    const reference = baseline.energy;
    const relative = signedRelativeDelta(current, reference);
    const defect = policy.energy === 'conserved' ? Math.abs(relative) : Math.max(0, relative);
    const metric = makeMetric({
      mode: policy.energy,
      current,
      reference,
      signedDelta: current - reference,
      absoluteDefect: defect,
      tolerances: {
        warn: policy.energy === 'conserved' ? tolerances.energyWarn : tolerances.energyWarn,
        critical: tolerances.energyCritical,
      },
      label: '机械能',
    });
    report.energy = metric;
    if (metric.severity !== 'ok') {
      report.warnings.push({
        quantity: 'energy',
        message:
          policy.energy === 'conserved'
            ? `机械能相对偏差 ${(defect * 100).toFixed(3)}%，超过 ${(metric.tolerance * 100).toFixed(2)}% 阈值`
            : `机械能异常增加 ${(defect * 100).toFixed(3)}%，耗散系统不应出现能量增益`,
      });
    }
  }

  if (metrics.momentum && policy.momentum) {
    const current = metrics.momentum.total;
    const reference = baseline.momentum;
    const relative = signedRelativeDelta(current, reference);
    const defect = Math.abs(relative);
    const metric = makeMetric({
      mode: policy.momentum,
      current,
      reference,
      signedDelta: current - reference,
      absoluteDefect: defect,
      tolerances: {
        warn: tolerances.momentumWarn,
        critical: tolerances.momentumCritical,
      },
      label: '总动量',
    });
    report.momentum = metric;
    if (metric.severity !== 'ok') {
      report.warnings.push({
        quantity: 'momentum',
        message: `总动量相对偏差 ${(defect * 100).toFixed(4)}%，超过 ${(metric.tolerance * 100).toFixed(4)}% 阈值`,
      });
    }
  }

  const severities = [report.energy?.severity, report.momentum?.severity].filter(Boolean);
  if (severities.includes('critical')) report.level = 'critical';
  else if (severities.includes('warning')) report.level = 'warning';
  report.ok = report.level === 'ok';
  return report;
}

export function worstDefect(report) {
  return Math.max(report.energy?.defect ?? 0, report.momentum?.defect ?? 0);
}
